import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { ClimateActionChapter1 } from '@/components/stories/climate-action/chapter-1';
import { ClimateActionChapter2 } from '@/components/stories/climate-action/chapter-2';
import { ClimateActionChapter3 } from '@/components/stories/climate-action/chapter-3';
import { ClimateActionChapter4 } from '@/components/stories/climate-action/chapter-4';
import { SurveyBackground } from '@/components/survey-background';
import data from '@/data/climate-action.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { t, Trans } from '@lingui/macro';
import { InferGetServerSidePropsType } from 'next';
import { useSharingImage } from '@/utils/use-sharing-image';
import { useLingui } from '@lingui/react';

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as ClimateActionData }),
  getStoryProps('climate-action', 'the-other-climate-crisis')
);

export default function ClimateAction(
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');
  useLingui();

  const sharingImage = useSharingImage('climate-action');
  const pageTitle = t({ id: 'climate-action.title', message: 'Climate action' });
  const pageDescription = t({
    id: 'climate-action.description',
    message:
      'Devastation wrought by the climate crisis is all around us. Some governments, companies and individuals are ramping up efforts to mitigate its effects. Will it be enough?',
  });

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.fire} hasBackButton>
        <StoryHeader backgroundId={storySlug} css={{ backgroundPosition: 'center bottom' }}>
          <StoryTheme>
            <Trans id="climate-action.theme">Climate Change</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="climate-action.tagline">
              <Text type="heading">
                Devastation wrought by the climate crisis is all around us. Some governments,
                companies, and individuals are ramping up efforts to mitigate its effects. Will it
                be <span>enough?</span>
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <SurveyBackground />

        <ClimateActionChapter1 />
        <ClimateActionChapter2 />
        {/* Yes, chapter 4 is before chapter 3, this is correct. */}
        <ClimateActionChapter4 data={data.strong_govt_action} />
        <ClimateActionChapter3 data={data.safer_when_coordinating_internationally} />

        <Outro
          sharing={{
            title: pageTitle,
            text: t({
              id: 'climate-action.sharing.text',
              message:
                'Devastation wrought by the climate crisis is all around us. Some governments, companies and individuals are ramping up efforts to mitigate its effects. Will it be enough?',
            }),
            url: sharingUrl,
          }}
          relatedStoryId={relatedStoryId}
        >
          <Trans id="climate-action.outro.text">
            What <AnnotatedType>action on climate change</AnnotatedType> would you like to see from
            your government?
          </Trans>
        </Outro>
      </Theme>
      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
